import React, { useRef, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../AuthContext";
import { useI18n } from "../i18n";
import { Smiley, SmileyMeh, SmileySad, X, ChatCircleDots, Image as ImageIcon, ArrowUUpLeft, Trash, ShareNetwork, PaperPlaneTilt, WarningCircle } from "@phosphor-icons/react";

const COUNTRIES = [
  { code: "IT", it: "Italia", en: "Italy", pt: "Itália" },
  { code: "PT", it: "Portogallo", en: "Portugal", pt: "Portugal" },
  { code: "ES", it: "Spagna", en: "Spain", pt: "Espanha" },
  { code: "FR", it: "Francia", en: "France", pt: "França" },
  { code: "DE", it: "Germania", en: "Germany", pt: "Alemanha" },
  { code: "GB", it: "Regno Unito", en: "United Kingdom", pt: "Reino Unido" },
  { code: "NL", it: "Paesi Bassi", en: "Netherlands", pt: "Países Baixos" },
  { code: "BE", it: "Belgio", en: "Belgium", pt: "Bélgica" },
  { code: "CH", it: "Svizzera", en: "Switzerland", pt: "Suíça" },
  { code: "AT", it: "Austria", en: "Austria", pt: "Áustria" },
  { code: "IE", it: "Irlanda", en: "Ireland", pt: "Irlanda" },
  { code: "OTHER", it: "Altro", en: "Other", pt: "Outro" },
];

// --- Redaction engine (isolated module; swappable for auto-detection in C5) ---
// applyRedactions is a pure function: given a loaded HTMLImageElement and a list
// of rectangles (in natural image coords), it returns a redacted JPEG data URL.
// A future auto-redactor only needs to produce `rects` before export.
export function applyRedactions(img, rects) {
  const c = document.createElement("canvas");
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0);
  ctx.fillStyle = "#000";
  rects.forEach((r) => ctx.fillRect(r.x, r.y, r.w, r.h));
  return c.toDataURL("image/jpeg", 0.82);
}

export function ScreenshotRedactor({ onChange }) {
  const { t } = useI18n();
  const [imgSrc, setImgSrc] = useState(null);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const [rects, setRects] = useState([]); // display coords
  const draw = useRef({ active: false, sx: 0, sy: 0, cur: null });
  const DISPLAY_W = 300;

  const redraw = useCallback((extra) => {
    const canvas = canvasRef.current, img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#000";
    rects.forEach((r) => ctx.fillRect(r.x, r.y, r.w, r.h));
    if (extra) { ctx.fillStyle = "rgba(0,0,0,0.7)"; ctx.fillRect(extra.x, extra.y, extra.w, extra.h); }
  }, [rects]);

  useEffect(() => { redraw(); }, [rects, redraw]);

  const exportImage = useCallback(() => {
    const img = imgRef.current, canvas = canvasRef.current;
    if (!img || !canvas) { onChange(null); return; }
    const scale = img.naturalWidth / canvas.width;
    const natRects = rects.map((r) => ({ x: r.x * scale, y: r.y * scale, w: r.w * scale, h: r.h * scale }));
    onChange(applyRedactions(img, natRects));
  }, [rects, onChange]);

  useEffect(() => { if (imgSrc) exportImage(); }, [rects, imgSrc, exportImage]);

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        imgRef.current = img;
        setRects([]);
        setImgSrc(reader.result); // canvas is sized in the effect below, once rendered
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  // Size + draw the canvas AFTER it is rendered (canvasRef only exists once imgSrc is set)
  useEffect(() => {
    const canvas = canvasRef.current, img = imgRef.current;
    if (!imgSrc || !canvas || !img) return;
    canvas.width = DISPLAY_W;
    canvas.height = Math.round((img.naturalHeight / img.naturalWidth) * DISPLAY_W);
    redraw();
    exportImage();
  }, [imgSrc, redraw, exportImage]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); const { x, y } = pos(e); draw.current = { active: true, sx: x, sy: y, cur: null }; };
  const move = (e) => {
    if (!draw.current.active) return;
    const { x, y } = pos(e);
    const r = { x: Math.min(x, draw.current.sx), y: Math.min(y, draw.current.sy), w: Math.abs(x - draw.current.sx), h: Math.abs(y - draw.current.sy) };
    draw.current.cur = r; redraw(r);
  };
  const end = () => {
    if (!draw.current.active) return;
    const r = draw.current.cur;
    draw.current.active = false;
    if (r && r.w > 4 && r.h > 4) setRects((rs) => [...rs, r]);
    else redraw();
  };

  const clearImg = () => { imgRef.current = null; setImgSrc(null); setRects([]); onChange(null); };

  return (
    <div data-testid="screenshot-redactor">
      <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200">
        <WarningCircle weight="fill" size={18} className="text-[var(--frumy-lock)] shrink-0 mt-0.5" aria-hidden />
        <p className="text-xs leading-relaxed text-amber-900">{t("fbScreenshotWarn")}</p>
      </div>
      {!imgSrc ? (
        <label data-testid="screenshot-choose" className="mt-3 cursor-pointer pill-btn inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]">
          <ImageIcon size={16} aria-hidden />{t("fbChooseImage")}
          <input type="file" accept="image/*" hidden onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      ) : (
        <div className="mt-3">
          <p className="text-xs text-[var(--text-muted)] mb-2">{t("fbRedactHint")}</p>
          <canvas ref={canvasRef} data-testid="redact-canvas"
            className="rounded-lg border border-[var(--frumy-border)] touch-none cursor-crosshair"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
          <div className="flex items-center gap-2 mt-2">
            <button type="button" data-testid="redact-undo" onClick={() => setRects((rs) => rs.slice(0, -1))}
              className="pill-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]">
              <ArrowUUpLeft size={13} aria-hidden />{t("fbUndo")}
            </button>
            <button type="button" data-testid="redact-clear" onClick={() => setRects([])}
              className="pill-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]">
              {t("fbClearRedact")}
            </button>
            <button type="button" data-testid="redact-remove" onClick={clearImg}
              className="pill-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">
              <Trash size={13} aria-hidden />{t("fbRemoveImage")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function FeedbackForm({ type = "parsing", defaultFormat = "", prefillMessage = "", onClose, onSent }) {
  const { t, lang } = useI18n();
  const [bank, setBank] = useState("");
  const [country, setCountry] = useState("");
  const [fileFormat, setFileFormat] = useState(defaultFormat || "");
  const [message, setMessage] = useState(prefillMessage || "");
  const [screenshot, setScreenshot] = useState(null);
  const [confirmRedact, setConfirmRedact] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!bank.trim()) { toast.error(t("fbBankRequired")); return; }
    if (!country) { toast.error(t("fbCountryRequired")); return; }
    if (screenshot && !confirmRedact) { toast.error(t("fbConfirmRequired")); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/feedback`, {
        type, bank: bank.trim(), country,
        file_format: fileFormat, message: message.trim(),
        screenshot: confirmRedact ? screenshot : null,
      });
      toast.success(t("fbSent"));
      onSent && onSent();
      onClose && onClose();
    } catch { toast.error(t("errGeneric")); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} data-testid="feedback-form" className="frumy-card p-5 mt-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-bold text-lg text-[var(--heading)] flex items-center gap-2">
          <ChatCircleDots weight="fill" size={20} className="text-[var(--frumy-green)]" aria-hidden />{t("fbReportTitle")}
        </h3>
        {onClose && (
          <button type="button" data-testid="feedback-close" onClick={onClose}
            className="grid place-items-center h-8 w-8 rounded-md text-[var(--text-muted)] hover:bg-[var(--frumy-muted)]">
            <X size={16} aria-hidden />
          </button>
        )}
      </div>
      <div className="flex items-start gap-2.5 mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
        <ShareNetwork weight="fill" size={16} className="text-[var(--frumy-green)] shrink-0 mt-0.5" aria-hidden />
        <p className="text-xs leading-relaxed text-emerald-900">{t("fbNoBankData")}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div>
          <label className="text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("fbBank")} *</label>
          <input data-testid="fb-bank" value={bank} onChange={(e) => setBank(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]" />
        </div>
        <div>
          <label className="text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("fbCountry")} *</label>
          <select data-testid="fb-country" value={country} onChange={(e) => setCountry(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm bg-[var(--surface)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]">
            <option value="">{t("fbSelectCountry")}</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c[lang] || c.en}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-3">
        <label className="text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("fbFormat")}</label>
        <select data-testid="fb-format" value={fileFormat} onChange={(e) => setFileFormat(e.target.value)}
          className="mt-1 w-full rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm bg-[var(--surface)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]">
          <option value="">—</option>
          <option value="CSV">CSV</option>
          <option value="PDF">PDF</option>
        </select>
      </div>

      <div className="mt-3">
        <label className="text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("fbProblem")}</label>
        <textarea data-testid="fb-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
          className="mt-1 w-full rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]" />
      </div>

      <div className="mt-4">
        <label className="text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("fbScreenshot")}</label>
        <div className="mt-2"><ScreenshotRedactor onChange={setScreenshot} /></div>
        {screenshot && (
          <label data-testid="fb-confirm-redact" className="flex items-center gap-2 mt-3 text-sm text-[var(--heading)] cursor-pointer">
            <input type="checkbox" checked={confirmRedact} onChange={(e) => setConfirmRedact(e.target.checked)} className="h-4 w-4" />
            {t("fbConfirmRedact")}
          </label>
        )}
      </div>

      <button type="submit" data-testid="fb-submit" disabled={saving}
        className="pill-btn cta-metal w-full mt-5 inline-flex items-center justify-center gap-2 text-white font-bold py-3 disabled:opacity-60">
        <PaperPlaneTilt weight="fill" size={16} aria-hidden />{saving ? "..." : t("fbSend")}
      </button>
    </form>
  );
}

const RATE_KEY = "frumy_rating_dismissed_at";

export function RatingCard() {
  const { t } = useI18n();
  const [rating, setRating] = useState(null);
  const [message, setMessage] = useState("");
  const [done, setDone] = useState(false);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    const ts = Number(localStorage.getItem(RATE_KEY) || 0);
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    setHidden(ts && days < 14);
  }, []);

  const dismiss = () => { localStorage.setItem(RATE_KEY, String(Date.now())); setHidden(true); };

  const choose = async (r) => {
    setRating(r);
    if (r === "happy") {
      try { await axios.post(`${API}/feedback`, { type: "rating", rating: r }); } catch { /* silent */ }
    }
  };

  const sendText = async () => {
    try { await axios.post(`${API}/feedback`, { type: "rating", rating, message: message.trim() }); } catch { /* silent */ }
    setDone(true);
    localStorage.setItem(RATE_KEY, String(Date.now()));
  };

  const share = async () => {
    try { await navigator.clipboard.writeText("https://frumy.app"); toast.success(t("fbLinkCopied")); } catch { /* ignore */ }
  };

  if (hidden) return null;

  return (
    <div data-testid="rating-card" className="frumy-card p-5 mt-10 relative">
      <button data-testid="rating-dismiss" onClick={dismiss}
        className="absolute top-3 right-3 grid place-items-center h-8 w-8 rounded-md text-[var(--text-muted)] hover:bg-[var(--frumy-muted)]">
        <X size={16} aria-hidden />
      </button>
      {done ? (
        <p className="font-bold text-[var(--heading)]">{t("fbThanks")}</p>
      ) : (
        <>
          <h3 className="font-bold text-lg text-[var(--heading)]">{t("fbRateTitle")}</h3>
          <div className="flex items-center gap-3 mt-3">
            {[
              { r: "sad", Icon: SmileySad },
              { r: "neutral", Icon: SmileyMeh },
              { r: "happy", Icon: Smiley },
            ].map(({ r, Icon }) => (
              <button key={r} data-testid={`rate-${r}`} onClick={() => choose(r)}
                className={`grid place-items-center h-12 w-12 rounded-full border transition-colors ${rating === r ? "bg-[var(--frumy-green)] text-white border-transparent" : "border-[var(--frumy-border)] text-[var(--text-muted)] hover:bg-[var(--frumy-muted)]"}`}>
                <Icon weight={rating === r ? "fill" : "regular"} size={26} aria-hidden />
              </button>
            ))}
          </div>

          {rating === "happy" && (
            <div className="mt-4" data-testid="rating-happy">
              <p className="font-bold text-[var(--heading)]">{t("fbThanks")}</p>
              <label className="block text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)] mt-3">{t("fbSuggestFn")}</label>
              <textarea data-testid="rating-suggestion" value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
                className="mt-1 w-full rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]" />
              <div className="flex flex-wrap gap-2 mt-3">
                <button data-testid="rating-send" onClick={sendText}
                  className="pill-btn inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]">
                  <PaperPlaneTilt size={14} aria-hidden />{t("fbSend")}
                </button>
                <button data-testid="rating-share" onClick={share}
                  className="pill-btn cta-metal inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white">
                  <ShareNetwork weight="fill" size={14} aria-hidden />{t("fbShare")}
                </button>
              </div>
            </div>
          )}

          {(rating === "neutral" || rating === "sad") && (
            <div className="mt-4" data-testid="rating-negative">
              <label className="block text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("fbWhatWrong")}</label>
              <textarea data-testid="rating-text" value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                className="mt-1 w-full rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]" />
              <button data-testid="rating-send" onClick={sendText}
                className="pill-btn cta-metal inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white mt-3">
                <PaperPlaneTilt weight="fill" size={14} aria-hidden />{t("fbSend")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
